import { useEffect, useState } from "react";

import {
  configurarDivisaoReserva,
  corrigirPagamentosReserva,
  encerrarSaldoReserva,
  loadPagamentosReserva,
  receberParcelaReserva,
  receberTodasParcelasReserva,
} from "../quadras.service";

import type {
  CorrecaoParcelaReserva,
  ResumoPagamentoReserva,
} from "../quadras.service";

import type { Booking, FormaPagamentoQuadra } from "../types";

import "./ReservationPaymentPanel.css";

type Props = {
  booking: Booking;
  disabled?: boolean;
  canCorrect?: boolean;
  onIntegral: (forma: FormaPagamentoQuadra) => Promise<void>;
  onChanged: () => Promise<void> | void;
};

const PAYMENT_OPTIONS: Array<{
  value: FormaPagamentoQuadra;
  label: string;
}> = [
  { value: "pix", label: "Pix" },
  { value: "dinheiro", label: "Dinheiro" },
  { value: "debito", label: "Débito" },
  { value: "credito", label: "Crédito" },
];

function formatBRL(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export default function ReservationPaymentPanel({
  booking,
  disabled = false,
  canCorrect = false,
  onIntegral,
  onChanged,
}: Props) {
  const [mode, setMode] =
    useState<"integral" | "dividido">("integral");
  const [paymentMethod, setPaymentMethod] =
    useState<FormaPagamentoQuadra>("pix");
  const [peopleCount, setPeopleCount] = useState(2);
  const [names, setNames] = useState(["", ""]);
  const [summary, setSummary] =
    useState<ResumoPagamentoReserva | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showCloseBalance, setShowCloseBalance] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTotal, setEditTotal] = useState(Number(booking.price ?? 0));
  const [editReason, setEditReason] = useState("");
  const [editRows, setEditRows] = useState<CorrecaoParcelaReserva[]>([]);

  async function refresh() {
    const result = await loadPagamentosReserva(booking.id);
    setSummary(result);
    if (result.parcelas.length > 0) setMode("dividido");
  }

  useEffect(() => {
    refresh().catch((err) => {
      console.error(err);
      setError("Não foi possível carregar os pagamentos.");
    });
  }, [booking.id]);

  function changePeopleCount(value: number) {
    const count = Math.max(2, Math.min(20, value || 2));
    setPeopleCount(count);
    setNames((current) =>
      Array.from({ length: count }, (_, index) => current[index] ?? ""),
    );
  }

  async function run(action: () => Promise<unknown>) {
    try {
      setBusy(true);
      setError("");
      await action();
      await refresh();
      await onChanged();
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível concluir a operação.",
      );
    } finally {
      setBusy(false);
    }
  }

  const hasSplit = Boolean(summary?.parcelas.length);
  const pending = summary?.parcelas.filter(
    (item) => item.status === "pendente",
  ) ?? [];

  function startCorrection() {
    const source = summary?.parcelas.length
      ? summary.parcelas.map((item) => ({
          id: item.id,
          nome: item.nome,
          valor_previsto: Number(item.valor_previsto),
          valor_pago: Number(item.valor_pago),
          status: item.status,
          forma_pagamento: item.forma_pagamento,
        }))
      : [{
          id: null,
          nome: booking.cliente,
          valor_previsto: Number(booking.price ?? 0),
          valor_pago: booking.pago ? Number(booking.price ?? 0) : 0,
          status: booking.pago ? "pago" as const : "pendente" as const,
          forma_pagamento: booking.forma_pagamento ?? null,
        }];

    setEditTotal(Number(booking.price ?? 0));
    setEditRows(source);
    setEditReason("");
    setEditing(true);
  }

  async function saveCorrection() {
    const planned = editRows.reduce(
      (total, item) => total + Number(item.valor_previsto || 0),
      0,
    );
    const paid = editRows.reduce(
      (total, item) => total + Number(item.valor_pago || 0),
      0,
    );

    if (!editReason.trim() || editReason.trim().length < 5) {
      setError("Informe o motivo da correção com pelo menos 5 caracteres.");
      return;
    }
    if (editTotal < 0 || Math.abs(planned - editTotal) > 0.009) {
      setError("A soma dos valores previstos deve ser igual ao novo total.");
      return;
    }
    if (paid - editTotal > 0.009) {
      setError("O total pago não pode ultrapassar o valor da reserva.");
      return;
    }
    if (editRows.some((item) =>
      !item.nome.trim() ||
      item.valor_previsto < 0 ||
      item.valor_pago < 0 ||
      (item.status === "pago" && (!item.forma_pagamento || item.valor_pago <= 0))
    )) {
      setError("Revise os nomes, valores, status e formas de pagamento.");
      return;
    }

    await run(async () => {
      await corrigirPagamentosReserva({
        reservaId: booking.id,
        valorTotal: editTotal,
        motivo: editReason.trim(),
        parcelas: editRows,
      });
      setEditing(false);
    });
  }

  return (
    <div className="reservation-payments">
      {!booking.pago && <div className="reservation-payment-tabs">
        <button
          type="button"
          className={mode === "integral" ? "active" : ""}
          onClick={() => setMode("integral")}
          disabled={disabled || busy || hasSplit}
        >
          Pagamento integral
        </button>
        <button
          type="button"
          className={mode === "dividido" ? "active" : ""}
          onClick={() => setMode("dividido")}
          disabled={disabled || busy}
        >
          Dividir pagamento
        </button>
      </div>}

      {!booking.pago && <div className="reservation-payment-methods">
        {PAYMENT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={paymentMethod === option.value ? "active" : ""}
            onClick={() => setPaymentMethod(option.value)}
            disabled={disabled || busy}
          >
            {option.label}
          </button>
        ))}
      </div>}

      {!booking.pago && mode === "integral" && !hasSplit && (
        <button
          type="button"
          className="reservation-receive-all"
          disabled={disabled || busy}
          onClick={() => run(() => onIntegral(paymentMethod))}
        >
          {busy
            ? "Recebendo..."
            : `Receber ${formatBRL(Number(booking.price ?? 0))}`}
        </button>
      )}

      {!booking.pago && mode === "dividido" && !hasSplit && (
        <div className="reservation-split-setup">
          <label>
            Quantidade de pessoas
            <input
              type="number"
              min="2"
              max="20"
              value={peopleCount}
              onChange={(event) =>
                changePeopleCount(Number(event.target.value))
              }
              disabled={disabled || busy}
            />
          </label>

          <div className="reservation-name-grid">
            {names.map((name, index) => (
              <label key={index}>
                Pessoa {index + 1}
                <input
                  value={name}
                  placeholder={`Nome da pessoa ${index + 1}`}
                  onChange={(event) =>
                    setNames((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? event.target.value : item,
                      ),
                    )
                  }
                  disabled={disabled || busy}
                />
              </label>
            ))}
          </div>

          <button
            type="button"
            className="reservation-create-split"
            disabled={
              disabled || busy || names.some((name) => !name.trim())
            }
            onClick={() =>
              run(() =>
                configurarDivisaoReserva(
                  booking.id,
                  names.map((name) => name.trim()),
                ),
              )
            }
          >
            Criar divisão
          </button>
        </div>
      )}

      {hasSplit && summary && (
        <>
          <div className="reservation-payment-summary-grid">
            <div><span>Total</span><strong>{formatBRL(summary.total)}</strong></div>
            <div><span>Recebido</span><strong>{formatBRL(summary.recebido)}</strong></div>
            <div><span>Saldo</span><strong>{formatBRL(summary.saldo)}</strong></div>
          </div>

          <div className="reservation-installments">
            {summary.parcelas.map((item) => (
              <div key={item.id} className="reservation-installment">
                <div>
                  <strong>{item.nome}</strong>
                  <span>{formatBRL(item.valor_previsto)}</span>
                </div>
                {item.status === "pendente" ? (
                  <button
                    type="button"
                    disabled={disabled || busy}
                    onClick={() =>
                      run(() => receberParcelaReserva(item.id, paymentMethod))
                    }
                  >
                    Marcar como pago
                  </button>
                ) : (
                  <span className={`installment-${item.status}`}>
                    {item.status === "pago" ? "Pago" : "Cortesia"}
                  </span>
                )}
              </div>
            ))}
          </div>

          {pending.length > 0 && (
            <div className="reservation-split-actions">
              <button
                type="button"
                className="reservation-receive-all"
                disabled={disabled || busy}
                onClick={() =>
                  run(() =>
                    receberTodasParcelasReserva(booking.id, paymentMethod),
                  )
                }
              >
                Receber todos os pendentes
              </button>

              {!showCloseBalance ? (
                <button
                  type="button"
                  className="reservation-close-balance"
                  onClick={() => setShowCloseBalance(true)}
                  disabled={disabled || busy}
                >
                  Encerrar com saldo
                </button>
              ) : (
                <div className="reservation-balance-options">
                  <p>O que fazer com o saldo de {formatBRL(summary.saldo)}?</p>
                  <button
                    type="button"
                    disabled={disabled || busy}
                    onClick={() =>
                      run(() => encerrarSaldoReserva(booking.id, "divida"))
                    }
                  >
                    Manter como dívida
                  </button>
                  <button
                    type="button"
                    disabled={disabled || busy}
                    onClick={() =>
                      run(() => encerrarSaldoReserva(booking.id, "desconto"))
                    }
                  >
                    Desconto/cortesia
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {canCorrect && (!editing ? (
        <button
          type="button"
          className="reservation-correct-payments"
          onClick={startCorrection}
          disabled={disabled || busy}
        >
          Corrigir valor e pagamentos
        </button>
      ) : (
        <div className="reservation-correction">
          <h4>Correção financeira da reserva</h4>
          <label>
            Novo valor total
            <input
              type="number"
              min="0"
              step="0.01"
              value={editTotal}
              onChange={(event) => setEditTotal(Number(event.target.value))}
            />
          </label>

          <div className="reservation-correction-rows">
            {editRows.map((item, index) => (
              <div className="reservation-correction-row" key={item.id ?? `new-${index}`}>
                <input
                  value={item.nome}
                  placeholder="Nome"
                  onChange={(event) => setEditRows((current) => current.map((row, rowIndex) =>
                    rowIndex === index ? { ...row, nome: event.target.value } : row,
                  ))}
                />
                <label>
                  Previsto
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.valor_previsto}
                    onChange={(event) => setEditRows((current) => current.map((row, rowIndex) =>
                      rowIndex === index ? { ...row, valor_previsto: Number(event.target.value) } : row,
                    ))}
                  />
                </label>
                <select
                  value={item.status}
                  onChange={(event) => setEditRows((current) => current.map((row, rowIndex) =>
                    rowIndex === index
                      ? {
                          ...row,
                          status: event.target.value as CorrecaoParcelaReserva["status"],
                          valor_pago: event.target.value === "pago" ? row.valor_pago : 0,
                          forma_pagamento: event.target.value === "pago" ? row.forma_pagamento : null,
                        }
                      : row,
                  ))}
                >
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                  <option value="desconto">Cortesia/desconto</option>
                </select>
                {item.status === "pago" && (
                  <>
                    <label>
                      Pago
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.valor_pago}
                        onChange={(event) => setEditRows((current) => current.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, valor_pago: Number(event.target.value) } : row,
                        ))}
                      />
                    </label>
                    <select
                      value={item.forma_pagamento ?? ""}
                      onChange={(event) => setEditRows((current) => current.map((row, rowIndex) =>
                        rowIndex === index
                          ? { ...row, forma_pagamento: event.target.value as FormaPagamentoQuadra }
                          : row,
                      ))}
                    >
                      <option value="">Forma de pagamento</option>
                      {PAYMENT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </>
                )}
                {editRows.length > 1 && (
                  <button
                    type="button"
                    className="reservation-remove-row"
                    onClick={() => setEditRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    Remover
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="reservation-add-row"
            onClick={() => setEditRows((current) => [
              ...current,
              {
                id: null,
                nome: "",
                valor_previsto: 0,
                valor_pago: 0,
                status: "pendente",
                forma_pagamento: null,
              },
            ])}
          >
            + Adicionar pessoa
          </button>

          <label>
            Motivo da correção
            <textarea
              value={editReason}
              onChange={(event) => setEditReason(event.target.value)}
              placeholder="Explique por que os valores foram alterados"
            />
          </label>

          <div className="reservation-correction-actions">
            <button type="button" onClick={() => setEditing(false)} disabled={busy}>
              Cancelar
            </button>
            <button type="button" onClick={saveCorrection} disabled={busy}>
              {busy ? "Salvando..." : "Salvar correção"}
            </button>
          </div>
        </div>
      ))}

      {error && <div className="booking-form-error">{error}</div>}
    </div>
  );
}
